import React from 'react';
import { DatePicker } from 'antd';
import moment, { Moment } from 'moment';
import { dateRangeFilterSummary } from './insightsMatchBundleFilters';

interface Props {
    dateFrom?: string | null;
    dateTo?: string | null;
    onChange: (next: { dateFrom: string | null; dateTo: string | null }) => void;
}

/**
 * Compact date-range control for Insights map filters.
 * Trigger shows Date + summary (All / "12 Aug - 20 Aug"); panel is antd RangePicker.
 * Cleared range = all dates (same as empty multi-select).
 */
const InsightsDateRangeFilter: React.FC<Props> = ({ dateFrom, dateTo, onChange }) => {
    const [open, setOpen] = React.useState(false);
    const rootRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        if (!open) return;
        const onDoc = (event: MouseEvent) => {
            const target = event.target as Node | null;
            if (!target) return;
            if (rootRef.current?.contains(target)) return;
            // antd calendar / range popup portals outside this root.
            if (
                target instanceof Element &&
                target.closest('.ant-picker-dropdown, .ant-picker-panel-container')
            ) {
                return;
            }
            setOpen(false);
        };
        document.addEventListener('mousedown', onDoc);
        return () => document.removeEventListener('mousedown', onDoc);
    }, [open]);

    const summary = dateRangeFilterSummary(dateFrom, dateTo);
    const value: [Moment, Moment] | null =
        dateFrom && dateTo
            ? [moment(dateFrom, 'YYYY-MM-DD'), moment(dateTo, 'YYYY-MM-DD')]
            : null;

    return (
        <div className={`insightsMultiSelect insightsDateRange${open ? ' isOpen' : ''}`} ref={rootRef}>
            <button
                type="button"
                className="insightsMultiSelectTrigger"
                aria-haspopup="dialog"
                aria-expanded={open}
                onClick={() => setOpen((v) => !v)}
            >
                <span className="insightsMultiSelectLabel">Date</span>
                <span className="insightsMultiSelectSummary">{summary}</span>
                <span className="insightsMultiSelectCaret" aria-hidden>
                    ▾
                </span>
            </button>
            {open ? (
                <div className="insightsMultiSelectPanel insightsDateRangePanel" role="dialog" aria-label="Date range">
                    <div className="insightsMultiSelectActions">
                        <button
                            type="button"
                            onClick={() => {
                                onChange({ dateFrom: null, dateTo: null });
                            }}
                        >
                            Clear
                        </button>
                    </div>
                    <DatePicker.RangePicker
                        allowClear
                        value={value}
                        onChange={(dates) => {
                            if (dates && dates[0] && dates[1]) {
                                onChange({
                                    dateFrom: dates[0].format('YYYY-MM-DD'),
                                    dateTo: dates[1].format('YYYY-MM-DD'),
                                });
                            } else {
                                onChange({ dateFrom: null, dateTo: null });
                            }
                        }}
                        format="DD MMM YYYY"
                        placeholder={['From', 'To']}
                        style={{ width: '100%' }}
                    />
                </div>
            ) : null}
        </div>
    );
};

export default InsightsDateRangeFilter;
